import type { StateCreator } from "zustand";
import type {
  BaseBlock,
  Pages,
} from "@circle-react-shared/BuilderUI/DataModel.types";
import type { Middlewares, Mutators } from "./index";
import type {
  BuilderUIState,
  GoogleFont,
  GoogleFontWeight,
  GoogleFontWithWeights,
  InitialData,
  PagesState,
} from "./types";

export type PagesSlice = StateCreator<
  BuilderUIState,
  Middlewares,
  Mutators,
  PagesState
>;

const defineInitialPage = (initialData: InitialData) => {
  if (initialData.pages && initialData.initialPageId) {
    return {
      initialPages: initialData.pages,
      initialPageId: initialData.initialPageId,
    };
  }

  if (initialData.block) {
    const block = initialData.block;
    return { initialPages: { [block.id]: block }, initialPageId: block.id };
  }

  throw new Error("No initial data provided");
};

export const createPagesSlice =
  (initialData: InitialData): PagesSlice =>
  (set, get) => {
    const { initialPages, initialPageId } = defineInitialPage(initialData);

    return {
      pages: initialPages,
      currentPageId: initialPageId,
      googleFonts: new Map<string, GoogleFontWithWeights>(),

      getBlocks: () => get().pages[get().currentPageId],

      setPages: (pages: Pages) => {
        const currentPageId = get().currentPageId;

        if (Object.keys(pages).includes(currentPageId)) {
          set({ pages }, undefined, "pages/setPages");
        } else {
          const firstPageId = Object.keys(pages)[0];
          set(
            { pages, currentPageId: firstPageId },
            undefined,
            "pages/setPages/firstPage",
          );
        }
      },

      goToPage: (pageId: string) => {
        set({ currentPageId: pageId }, undefined, "pages/goToPage");
      },

      addPage: (blocks: BaseBlock) => {
        const currentPages = get().pages;

        if (currentPages[blocks.id]) {
          throw new Error(
            `[BuilderUI] - Page with the id: ${blocks.id} already exists.`,
          );
        }

        set(
          {
            pages: { ...currentPages, [blocks.id]: blocks },
          },
          undefined,
          "pages/addPage",
        );
      },

      addAndGoToPage: (blocks: BaseBlock) => {
        const addPage = get().addPage;
        const goToPage = get().goToPage;

        addPage(blocks);
        goToPage(blocks.id);
      },

      deletePage: (pageId: string) => {
        const { pages, currentPageId } = get();

        if (!pages[pageId]) {
          console.error(`[BuilderUI] - Page ${pageId} does not exist.`);
          return;
        }

        const newPages = { ...pages };
        delete newPages[pageId];

        const hasPagesAfterDeletion = Object.keys(newPages).length > 0;

        if (!hasPagesAfterDeletion) {
          set(
            {
              pages: initialPages,
              currentPageId: initialPageId,
            },
            undefined,
            "pages/deletePage/noPages",
          );
          return;
        }

        const isCurrentPageDeleted = pageId === currentPageId;

        if (isCurrentPageDeleted) {
          const firstPage = Object.keys(newPages)[0];
          set(
            {
              pages: newPages,
              currentPageId: firstPage,
            },
            undefined,
            "pages/deletePage/currentPageDeleted",
          );
          return;
        }

        set(
          { pages: newPages },
          undefined,
          "pages/deletePage/otherPageDeleted",
        );
      },

      updateBlocksFromPage: (pageId: string, blocks: BaseBlock) => {
        const { pages } = get();

        if (!(pageId in pages))
          throw new Error(`PageId: ${pageId} does not exist in the state.`);

        set(
          { pages: { ...pages, [pageId]: blocks } },
          undefined,
          "pages/updateBlocksFromPage",
        );
      },

      updateBlocksFromCurrentPage: (blocks: BaseBlock) => {
        const { pages, currentPageId } = get();
        set(
          { pages: { ...pages, [currentPageId]: blocks } },
          undefined,
          "pages/updateBlocksFromCurrentPage",
        );
      },

      addGoogleFont: (font: GoogleFont) => {
        const { googleFonts } = get();
        const newGoogleFonts = new Map(googleFonts);

        const existingFont = newGoogleFonts.get(font.family);
        if (existingFont) {
          font.variants.forEach(variant => existingFont.weights.add(variant));
          newGoogleFonts.set(font.family, existingFont);
        } else {
          newGoogleFonts.set(font.family, {
            family: font.family,
            weights: new Set(font.variants),
          });
        }

        set({ googleFonts: newGoogleFonts }, undefined, "pages/addGoogleFont");
      },

      addGoogleFontWeight: (fontWeight: GoogleFontWeight) => {
        const { googleFonts } = get();
        const newGoogleFonts = new Map(googleFonts);

        const existingFont = newGoogleFonts.get(fontWeight.family);
        if (existingFont) {
          existingFont.weights.add(fontWeight.weight);
          newGoogleFonts.set(fontWeight.family, existingFont);
        } else {
          newGoogleFonts.set(fontWeight.family, {
            family: fontWeight.family,
            weights: new Set([fontWeight.weight]),
          });
        }

        set(
          { googleFonts: newGoogleFonts },
          undefined,
          "pages/addGoogleFontWeight",
        );
      },
    };
  };
